'use client';

import { useEffect } from "react";
import styles from "./HistoryEventView.module.scss";

const REVEAL_SELECTOR = "[data-history-event-reveal]";
const CLEANUP_DELAY_MS = 3000;
const REVEAL_THRESHOLD = 0.15;

export default function HistoryEventReveal() {
    useEffect(() => {
        const elements = Array.from(document.querySelectorAll<HTMLElement>(REVEAL_SELECTOR));
        if (elements.length === 0) return;

        const cleanupTimers = new Map<HTMLElement, number>();
        const revealWithoutAnimation = () => {
            elements.forEach((element) => {
                element.classList.add(styles.revealVisible);
                element.classList.remove(styles.revealReady);
            });
        };
        const prefersReducedMotion = window.matchMedia?.("(prefers-reduced-motion: reduce)").matches ?? true;

        if (prefersReducedMotion || typeof IntersectionObserver === "undefined") {
            revealWithoutAnimation();
            return () => {
                elements.forEach((element) => {
                    element.classList.remove(styles.revealVisible);
                });
            };
        }

        const revealElement = (element: HTMLElement) => {
            element.classList.add(styles.revealVisible);
            cleanupTimers.set(
                element,
                window.setTimeout(() => {
                    element.classList.remove(styles.revealReady);
                    cleanupTimers.delete(element);
                }, CLEANUP_DELAY_MS)
            );
        };

        const headerHeight = parseFloat(getComputedStyle(document.documentElement).getPropertyValue("--header-height")) || 120;
        const observer = new IntersectionObserver(
            (entries) => {
                for (const entry of entries) {
                    if (!entry.isIntersecting || entry.intersectionRatio < REVEAL_THRESHOLD) continue;

                    const element = entry.target as HTMLElement;
                    revealElement(element);
                    observer.unobserve(element);
                }
            },
            {
                root: null,
                rootMargin: `${-headerHeight}px 0px 0px 0px`,
                threshold: [0, REVEAL_THRESHOLD],
            }
        );

        elements.forEach((element) => {
            element.classList.add(styles.revealReady);
            observer.observe(element);
        });

        return () => {
            observer.disconnect();
            cleanupTimers.forEach((timer) => {
                window.clearTimeout(timer);
            });
            elements.forEach((element) => {
                element.classList.remove(styles.revealReady, styles.revealVisible);
            });
        };
    }, []);

    return null;
}
